import {
  HomeEntityType,
  useCurrentWorkspace,
  useCustomQuery,
} from '@gripp/shared-logic';
import { rangeToDates } from './useHomeActivityMap';
import { TimeRange } from '../types';

/**
 * One map point for the plotting-mode toggle. Mirrors the generated
 * `HomeMapPoint`. `status` is a display label (asset status, or the
 * severity-derived activity bucket 'urgent'/'pending'/'solved'); the FE assigns
 * the marker colour from it.
 */
export interface HomeMapPoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  status: string | null;
}

const HOME_ENTITY_POINTS_QUERY = `
  query HomeEntityPoints($filter: HomeActivityFilter, $entityType: HomeEntityType!) {
    homeEntityPoints(filter: $filter, entityType: $entityType) {
      id
      name
      lat
      lng
      status
    }
  }
`;

/**
 * Fetches individual entity points for the map plotting-mode toggle, scoped to
 * the workspace + range. The resting map passes ASSET; selecting a card passes
 * that card's entity. Passing `null` disables the query (defensive — the map
 * always supplies a mode).
 *
 * Root query — reads `data.homeEntityPoints` directly. REAL and
 * workspace-scoped; ASSET/ACTIVITY can return a large bounded set, so the map
 * renders these clustered.
 */
export function useHomeEntityPoints(
  range: TimeRange,
  entityType: HomeEntityType | null
): {
  data: HomeMapPoint[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { startDate, endDate } = rangeToDates(range);

  const { data, isLoading, error } = useCustomQuery({
    cacheKey: ['homeEntityPoints', workspaceId, range, entityType],
    query: HOME_ENTITY_POINTS_QUERY,
    variables: {
      filter: {
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
      entityType,
    },
    options: {
      // Only fetch when a plotting mode is active and the workspace is known.
      enabled: !!workspaceId && !!entityType,
      staleTime: 60_000,
    },
  });

  return { data: data?.homeEntityPoints, isLoading, error: error as Error | null };
}
